import React, { useEffect, useMemo, useRef, useState } from "react";

function deviceLabel(device) {
  const did = device?.did ?? "";
  const aid = device?.aid ?? "";
  return aid ? `${did} / ${aid}` : String(did);
}

function buttonText(devices, value, placeholder) {
  if (!value.length) return placeholder;
  if (value.length === 1) {
    const match = devices.find((device) => String(device.did) === String(value[0]));
    return match ? deviceLabel(match) : String(value[0]);
  }
  return `${value.length} selected`;
}

function DeviceSelect({
  devices = [],
  value = [],
  onChange,
  multiple = false,
  placeholder = "Device ID / Asset ID",
  className = "",
}) {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, []);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return devices;
    return devices.filter((device) => deviceLabel(device).toLowerCase().includes(needle));
  }, [devices, query]);

  const choose = (did) => {
    const id = did;
    const selected = value.some((item) => String(item) === String(id));
    if (!multiple) {
      onChange(selected ? [] : [id]);
      setOpen(false);
      setQuery("");
      return;
    }
    onChange(selected ? value.filter((item) => String(item) !== String(id)) : [...value, id]);
  };

  return (
    <div ref={rootRef} className={`relative mb-2 inline-block w-full text-left md:mb-0 md:w-72 ${className}`}>
      <button
        type="button"
        onClick={() => {
          setQuery("");
          setOpen((current) => !current);
        }}
        className="inline-flex w-full items-center justify-between rounded-md bg-lightPrimary px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-navy-900 dark:text-white"
      >
        <span className="truncate">{buttonText(devices, value, placeholder)}</span>
        <svg className="-mr-1 ml-2 h-5 w-5 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.292 7.707a1 1 0 011.414 0L10 11.002l3.293-3.295a1 1 0 011.414 1.414l-4 4a 1 1 0 01-1.414 0l-4-4a 1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-2 w-full origin-top-right rounded-md bg-lightPrimary shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-navy-900">
          <div className="border-b border-gray-200 p-2 dark:border-white/10">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search device or asset"
              className="w-full rounded-md bg-white px-3 py-2 text-sm text-navy-700 outline-none ring-1 ring-gray-200 placeholder:text-gray-400 focus:ring-brand-500 dark:bg-navy-800 dark:text-white dark:ring-white/10"
            />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length ? (
              filtered.map((device) => {
                const checked = value.some((item) => String(item) === String(device.did));
                return (
                  <label
                    key={device.did}
                    className="flex cursor-pointer items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-white dark:hover:bg-navy-800"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => choose(device.did)}
                      className="form-checkbox h-4 w-4 text-indigo-600"
                    />
                    <span className="ml-2">{deviceLabel(device)}</span>
                  </label>
                );
              })
            ) : (
              <p className="px-4 py-2 text-sm text-gray-500">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DeviceSelect;
